import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { plannerAPI } from "../../api/client";
import { formatLocalDate, parseLocalDate } from "../../utils/dateUtils";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";
import useStatusColors from "../../hooks/useStatusColors";
import UnscheduledTaskList from "./UnscheduledTaskList";

const SLOT_HEIGHT = 60;
const TIME_COL_WIDTH = 64;
const USER_COL_WIDTH = 200;
// A lane narrower than this ellipsises every label down to "Pr...", so a person
// working on several things at once gets a wider column instead of thinner
// cards. The grid already scrolls sideways; unreadable cards cost more than
// scrolling does.
const MIN_LANE_WIDTH = 140;
const LANE_GAP = 4;
const COL_PADDING = 4;
const MIN_TASK_HEIGHT = 28;
const SNAP_MINUTES = 30;

function parseTimeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToTimeStr(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatTimeDisplay(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH}:${String(m).padStart(2, "0")} ${period}`;
}

function getUserId(user) {
  return user?.userId || user?.id;
}

function getRoleLabel(user) {
  if (!user?.role) return "";
  if (typeof user.role === "string") return user.role;
  return user.role?.name || "";
}

function getTaskStyle(task, globalStartMins, workStartTime, workEndTime) {
  if (!task?.scheduledStartTime) return null;

  const startDate = new Date(task.scheduledStartTime);
  const startMins = startDate.getHours() * 60 + startDate.getMinutes();
  const workStartM = parseTimeToMinutes(workStartTime || "09:00");
  const workEndM = parseTimeToMinutes(workEndTime || "18:00");

  const durationMins =
    task.durationUnit === "Hours"
      ? (task.estimatedDuration || 1) * 60
      : (task.estimatedDuration || 1) * (workEndM - workStartM);

  const top = ((startMins - globalStartMins) / 60) * SLOT_HEIGHT;
  const height = Math.max(MIN_TASK_HEIGHT, (durationMins / 60) * SLOT_HEIGHT);
  const isOvertimeTask = startMins + durationMins > workEndM;

  return { top, height, isOvertimeTask, startMins, durationMins };
}

// Tasks that run at the same time would otherwise be stacked at identical
// coordinates and render on top of each other. Clusters of overlapping tasks
// are split into side-by-side lanes, each cluster only as narrow as it needs.
function layoutTaskLanes(entries) {
  const sorted = [...entries].sort(
    (a, b) => a.style.startMins - b.style.startMins,
  );

  const clusters = [];
  let current = null;
  let clusterEnd = -Infinity;

  sorted.forEach((entry) => {
    const end = entry.style.startMins + entry.style.durationMins;
    if (!current || entry.style.startMins >= clusterEnd) {
      current = [];
      clusters.push(current);
      clusterEnd = end;
    } else {
      clusterEnd = Math.max(clusterEnd, end);
    }
    current.push(entry);
  });

  return clusters.flatMap((cluster) => {
    const laneEnds = [];
    const placed = cluster.map((entry) => {
      const end = entry.style.startMins + entry.style.durationMins;
      let column = laneEnds.findIndex(
        (laneEnd) => laneEnd <= entry.style.startMins,
      );
      if (column === -1) {
        column = laneEnds.length;
        laneEnds.push(end);
      } else {
        laneEnds[column] = end;
      }
      return { ...entry, column };
    });
    return placed.map((entry) => ({ ...entry, columnCount: laneEnds.length }));
  });
}

function DayStatusBadge({ type, holidayName, overrideNote }) {
  if (type === "WEEKLY_OFF") {
    return (
      <span className="day-status-badge day-status-badge--off">Weekly Off</span>
    );
  }
  if (type === "OFF") {
    return (
      <span className="day-status-badge day-status-badge--leave">
        {overrideNote || "Leave"}
      </span>
    );
  }
  if (type === "HALF_DAY") {
    return (
      <span className="day-status-badge day-status-badge--half">Half Day</span>
    );
  }
  if (type === "HOLIDAY") {
    return (
      <span
        className="day-status-badge day-status-badge--holiday"
        title={holidayName || "Public Holiday"}
      >
        {holidayName || "Public Holiday"}
      </span>
    );
  }
  return null;
}

function TaskBlock({
  task,
  style,
  onClick,
  getStatusStyle,
  draggable,
  isDragging,
  onDragStart,
  onDragEnd,
}) {
  const statusStyle = getStatusStyle(task.status, task.slaPercentage);
  const statusInfo = formatAssignmentStatus(task.status);

  return (
    <div
      className={[
        "team-task-block",
        style.isOvertimeTask ? "team-task-block--overtime" : "",
        isDragging ? "team-task-block--dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
        top: style.top,
        height: style.height,
        // Overrides the stylesheet's left/right pair so lanes can sit side by
        // side inside the user column.
        left: style.left,
        width: style.width,
        right: "auto",
        backgroundColor: statusStyle.backgroundColor,
        borderLeft: `4px solid ${statusStyle.borderColor}`,
      }}
      onClick={onClick}
      title={`${task.stageName || "Task"} - ${task.projectName || "Project"}${
        draggable ? " (drag to reschedule)" : ""
      }`}
    >
      <div
        className="team-task-project"
        style={{ color: statusStyle.textColor }}
      >
        {task.projectName || "Untitled Project"}
      </div>
      {style.height > 40 && (
        <div className="team-task-stage">{task.stageName || "Task"}</div>
      )}
      {style.height > 56 && (
        <div className="team-task-status">
          {statusInfo.icon} {statusInfo.label}
        </div>
      )}
    </div>
  );
}

export default function TeamDayView({
  focusDate,
  teamData,
  isAdmin,
  onRefresh,
}) {
  const navigate = useNavigate();
  const { getStatusStyle } = useStatusColors();

  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [ghost, setGhost] = useState(null);
  const [toast, setToast] = useState("");
  // Holds the new start time while the reschedule request is in flight, so the
  // card stays where it was dropped instead of snapping back until the refresh
  // lands.
  const [pendingTimes, setPendingTimes] = useState({});
  const gridBodyRef = useRef(null);
  const dragSourceRef = useRef(null);
  const toastTimerRef = useRef(null);

  useEffect(() => setPendingTimes({}), [teamData]);

  useEffect(
    () => () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    },
    [],
  );

  const showToast = (message) => {
    setToast(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(""), 3000);
  };

  const resetDrag = () => {
    setDraggingId(null);
    setGhost(null);
    setDragOffset(0);
    dragSourceRef.current = null;
  };

  const dateObj = useMemo(() => {
    if (focusDate instanceof Date) return focusDate;
    return parseLocalDate(focusDate) || new Date();
  }, [focusDate]);

  const dateStr = useMemo(() => formatLocalDate(dateObj), [dateObj]);

  const formattedDate = useMemo(
    () =>
      dateObj.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [dateObj],
  );

  const usersForDay = useMemo(() => {
    const users = teamData?.users || [];
    return users.map((user) => {
      const dayInfo = user?.days?.[dateStr] || null;
      const rawStart = dayInfo?.workStartTime || user?.workStartTime || null;
      const rawEnd = dayInfo?.workEndTime || user?.workEndTime || null;

      return {
        user,
        dayInfo,
        tasks: (dayInfo?.tasks || []).map((task) =>
          pendingTimes[task.id]
            ? { ...task, scheduledStartTime: pendingTimes[task.id] }
            : task,
        ),
        rawStart,
        rawEnd,
        workStartTime: rawStart || "09:00",
        workEndTime: rawEnd || "18:00",
      };
    });
  }, [dateStr, teamData, pendingTimes]);

  if (!teamData || usersForDay.length === 0) {
    return (
      <div className="team-day-view">
        <div className="team-day-header">
          <h3>{formattedDate}</h3>
          <span className="planner-view-label">0 team members</span>
        </div>
        <div className="day-empty">No team data available</div>
      </div>
    );
  }

  const hasScheduleData = usersForDay.some((u) => u.rawStart && u.rawEnd);
  const allStartMins = usersForDay.map((u) =>
    parseTimeToMinutes(u.workStartTime || "09:00"),
  );
  const allEndMins = usersForDay.map((u) =>
    parseTimeToMinutes(u.workEndTime || "18:00"),
  );

  const defaultStart = parseTimeToMinutes("09:00");
  const defaultEnd = parseTimeToMinutes("18:00");
  const fallbackStart = parseTimeToMinutes("08:00");
  const fallbackEnd = parseTimeToMinutes("20:00");

  const globalStartMins = hasScheduleData
    ? Math.min(...allStartMins, defaultStart)
    : fallbackStart;
  const globalEndMins = hasScheduleData
    ? Math.max(...allEndMins, defaultEnd)
    : fallbackEnd;
  const safeGlobalEndMins =
    globalEndMins > globalStartMins ? globalEndMins : globalStartMins + 60;

  const hourSlots = [];
  for (let m = globalStartMins; m <= safeGlobalEndMins; m += 60) {
    hourSlots.push({
      minutes: m,
      label: formatTimeDisplay(minutesToTimeStr(m)),
    });
  }

  const totalHeight =
    ((safeGlobalEndMins - globalStartMins) / 60) * SLOT_HEIGHT;

  // Each person's column is sized to its own busiest overlap, then placed at a
  // running offset — quiet days stay at the base width, a double-booked day
  // widens just enough to keep every card readable.
  let runningLeft = TIME_COL_WIDTH;
  const columns = usersForDay.map((entry) => {
    const isOff = ["WEEKLY_OFF", "OFF", "HOLIDAY"].includes(
      entry.dayInfo?.type,
    );
    const placedTasks = isOff
      ? []
      : layoutTaskLanes(
          entry.tasks
            .map((task) => ({
              task,
              style: getTaskStyle(
                task,
                globalStartMins,
                entry.workStartTime,
                entry.workEndTime,
              ),
            }))
            .filter(({ style }) => style !== null),
        );
    const laneCount = placedTasks.reduce(
      (max, placed) => Math.max(max, placed.columnCount),
      1,
    );
    const width = Math.max(
      USER_COL_WIDTH,
      laneCount * MIN_LANE_WIDTH + COL_PADDING * 2 + (laneCount - 1) * LANE_GAP,
    );
    const untimedTasks = isOff
      ? []
      : entry.tasks.filter((task) => !task.scheduledStartTime);
    const column = {
      ...entry,
      isOff,
      placedTasks,
      untimedTasks,
      laneCount,
      width,
      left: runningLeft,
    };
    runningLeft += width;
    return column;
  });

  const hasUntimed = columns.some((column) => column.untimedTasks.length);
  const openTask = (task) => {
    if (!task?.projectId || !task?.id) return;
    navigate(`/projects/${task.projectId}/stage/${task.id}`);
  };

  const laneWidth = (columnWidth, columnCount) =>
    (columnWidth - COL_PADDING * 2 - (columnCount - 1) * LANE_GAP) /
    columnCount;

  const totalWidth = runningLeft;
  const bandWidth = totalWidth - TIME_COL_WIDTH;

  // ── Drag to reschedule ───────────────────────────────────────────────────
  // A task moves in time, not between people: dropping it in someone else's
  // column would silently reassign the work, so only the owner's column
  // accepts the drop.
  const pointerMinutes = (clientY) => {
    const rect = gridBodyRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const relativeY = clientY - rect.top - dragOffset;
    const snapped =
      Math.round(((relativeY / SLOT_HEIGHT) * 60) / SNAP_MINUTES) *
        SNAP_MINUTES +
      globalStartMins;
    return Math.max(
      globalStartMins,
      Math.min(safeGlobalEndMins - SNAP_MINUTES, snapped),
    );
  };

  const handleTaskDragStart = (event, task, column) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDragOffset(event.clientY - rect.top);
    setDraggingId(task.id);
    dragSourceRef.current = { taskId: task.id, userId: getUserId(column.user) };
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", task.id);
  };

  const handleColumnDragOver = (event, column) => {
    if (!draggingId) return;
    const source = dragSourceRef.current;
    if (!source || source.userId !== getUserId(column.user)) return;
    event.preventDefault();
    const minutes = pointerMinutes(event.clientY);
    if (minutes === null) return;
    setGhost({
      userId: getUserId(column.user),
      top: ((minutes - globalStartMins) / 60) * SLOT_HEIGHT,
    });
  };

  const handleColumnDrop = async (event, column) => {
    const source = dragSourceRef.current;
    if (!source) return;
    event.preventDefault();

    if (source.userId !== getUserId(column.user)) {
      showToast("Drop a task in the same person's column to reschedule it.");
      resetDrag();
      return;
    }

    const task = column.tasks.find((item) => item.id === source.taskId);
    const minutes = pointerMinutes(event.clientY);
    if (!task || minutes === null) {
      resetDrag();
      return;
    }

    const newStart = new Date(task.scheduledStartTime);
    if (Number.isNaN(newStart.getTime())) {
      showToast("This task has no scheduled date. Reassign it first.");
      resetDrag();
      return;
    }
    newStart.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    const newStartIso = newStart.toISOString();

    const style = getTaskStyle(
      task,
      globalStartMins,
      column.workStartTime,
      column.workEndTime,
    );
    const workEndMins = parseTimeToMinutes(column.workEndTime || "18:00");
    const isOvertime = minutes + (style?.durationMins || 0) > workEndMins;

    const updated = column.tasks
      .map((item) =>
        item.id === task.id
          ? { ...item, scheduledStartTime: newStartIso, isOvertime }
          : item,
      )
      .filter((item) => item.scheduledStartTime)
      .sort(
        (a, b) =>
          new Date(a.scheduledStartTime) - new Date(b.scheduledStartTime),
      );

    setPendingTimes((current) => ({ ...current, [task.id]: newStartIso }));
    resetDrag();

    try {
      await plannerAPI.reorder({
        userId: source.userId,
        assignments: updated.map((item, index) => ({
          id: item.id,
          queuePosition: index + 1,
          scheduledStartTime: item.scheduledStartTime,
          isOvertime: item.isOvertime,
        })),
      });
      await onRefresh?.();
    } catch {
      setPendingTimes((current) => {
        const next = { ...current };
        delete next[task.id];
        return next;
      });
      showToast("Could not reschedule that task. Try again.");
    }
  };

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const isFocusToday = formatLocalDate(now) === dateStr;
  const showNowLine =
    isFocusToday && nowMins >= globalStartMins && nowMins <= safeGlobalEndMins;
  const nowTop = ((nowMins - globalStartMins) / 60) * SLOT_HEIGHT;

  return (
    <div className="team-day-view">
      <div className="team-day-header">
        <h3>{formattedDate}</h3>
        <span className="planner-view-label">
          {usersForDay.length} team members
        </span>
      </div>

      {toast && <div className="planner-toast">{toast}</div>}

      <div className="team-day-grid-wrapper">
        <div className="team-day-grid" style={{ width: totalWidth }}>
          <div className="team-day-user-headers">
            <div className="team-day-time-placeholder" />
            {columns.map(({ user, dayInfo, laneCount, width }) => (
              <div
                key={getUserId(user)}
                className="team-day-user-header-cell"
                style={{ width, minWidth: width }}
              >
                <div className="user-day-avatar">
                  {user?.firstName?.[0] || ""}
                  {user?.lastName?.[0] || ""}
                </div>
                <div className="user-day-name">
                  {user?.firstName || ""} {user?.lastName || ""}
                </div>
                <div className="user-day-role">{getRoleLabel(user)}</div>
                {laneCount > 1 && (
                  <span
                    className="user-day-overlap"
                    title="Tasks scheduled at the same time"
                  >
                    {laneCount} at once
                  </span>
                )}
                <DayStatusBadge
                  type={dayInfo?.type}
                  holidayName={dayInfo?.holidayName}
                  overrideNote={dayInfo?.overrideNote}
                />
              </div>
            ))}
          </div>

          {hasUntimed && (
            <div className="team-day-untimed-row">
              <div
                className="team-day-untimed-label"
                title="Booked for this day without a start time"
              >
                No time set
              </div>
              {columns.map((column) => (
                <div
                  key={getUserId(column.user)}
                  className="team-day-untimed-cell"
                  style={{ width: column.width, minWidth: column.width }}
                >
                  <UnscheduledTaskList
                    tasks={column.untimedTasks}
                    onOpen={openTask}
                  />
                </div>
              ))}
            </div>
          )}

          <div
            className="team-day-grid-body"
            ref={gridBodyRef}
            style={{ height: totalHeight, width: totalWidth }}
          >
            <div className="team-day-time-col">
              {hourSlots.map((slot) => (
                <div
                  key={slot.minutes}
                  className="team-day-hour-label"
                  style={{
                    top: ((slot.minutes - globalStartMins) / 60) * SLOT_HEIGHT,
                  }}
                >
                  {slot.label}
                </div>
              ))}
            </div>

            {hourSlots.map((slot) => (
              <div
                key={slot.minutes}
                className="team-day-hour-line"
                style={{
                  top: ((slot.minutes - globalStartMins) / 60) * SLOT_HEIGHT,
                  left: TIME_COL_WIDTH,
                  width: bandWidth,
                }}
              />
            ))}

            {showNowLine && (
              <div
                className="team-day-now-line"
                style={{
                  top: nowTop,
                  left: TIME_COL_WIDTH,
                  width: bandWidth,
                }}
              />
            )}

            {columns.map((column) => {
              const {
                user,
                isOff,
                placedTasks,
                width: colWidth,
                left: colLeft,
                workStartTime,
                workEndTime,
              } = column;
              const canDrop = isAdmin && !isOff;
              const workStartMins = parseTimeToMinutes(
                workStartTime || "09:00",
              );
              const workEndMins = parseTimeToMinutes(workEndTime || "18:00");
              const workBandTop =
                ((workStartMins - globalStartMins) / 60) * SLOT_HEIGHT;
              const workBandHeight =
                ((workEndMins - workStartMins) / 60) * SLOT_HEIGHT;
              const workEndTop =
                ((workEndMins - globalStartMins) / 60) * SLOT_HEIGHT;

              return (
                <div
                  key={getUserId(user)}
                  className="team-day-user-col"
                  style={{ left: colLeft, width: colWidth }}
                  onDragOver={
                    canDrop ? (e) => handleColumnDragOver(e, column) : undefined
                  }
                  onDrop={
                    canDrop ? (e) => handleColumnDrop(e, column) : undefined
                  }
                >
                  {ghost && ghost.userId === getUserId(user) && (
                    <div
                      className="team-drag-ghost"
                      style={{
                        top: ghost.top,
                        height:
                          placedTasks.find(({ task }) => task.id === draggingId)
                            ?.style.height || MIN_TASK_HEIGHT,
                      }}
                    />
                  )}
                  {isOff && <div className="team-day-off-overlay" />}

                  {!isOff && workBandHeight > 0 && (
                    <div
                      className="team-day-working-band"
                      style={{ top: workBandTop, height: workBandHeight }}
                    />
                  )}

                  {!isOff && (
                    <div
                      className="team-day-work-end-line"
                      style={{ top: workEndTop }}
                    />
                  )}

                  {placedTasks.map(
                    ({ task, style, column: laneIndex, columnCount }) => {
                      const width = laneWidth(colWidth, columnCount);
                      return (
                        <TaskBlock
                          key={task.id}
                          task={task}
                          style={{
                            ...style,
                            left: COL_PADDING + laneIndex * (width + LANE_GAP),
                            width,
                          }}
                          getStatusStyle={getStatusStyle}
                          draggable={canDrop}
                          isDragging={draggingId === task.id}
                          onDragStart={
                            canDrop
                              ? (e) => handleTaskDragStart(e, task, column)
                              : undefined
                          }
                          onDragEnd={canDrop ? resetDrag : undefined}
                          onClick={() => openTask(task)}
                        />
                      );
                    },
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
